import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import AppLogo from "@/components/AppLogo";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2 } from "lucide-react";

const DEMO_ACCOUNTS = [
  { label: "Super Admin", email: "info@infozit.com", password: "Password@123" },
  { label: "Team Lead", email: "lead@infozit.com", password: "Password@123" },
  { label: "Developer", email: "dev@infozit.com", password: "Password@123" },
  { label: "Viewer User", email: "viewer@infozit.com", password: "Password@123" },
];

export default function LoginPage() {
  const { user, login } = useAuth();
  // const [email, setEmail] = useState("info@infozit.com");
  // const [password, setPassword] = useState("Password@123");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!email) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!password) {
      newErrors.password = "Password is required";
    } else if (password.length < 6) {
      newErrors.password = "Password must be at least 6 characters long";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) {
      toast.error(res.error || "Login failed");
    }
  };

  const applyDemo = (a) => {
    setEmail(a.email);
    setPassword(a.password);
    setErrors({});
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="login-page">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 bg-primary text-primary-foreground p-12 relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <AppLogo className="w-12 h-12 rounded-md object-cover" />
            <div className="font-heading font-bold text-xl">Infoz IT Solutions</div>
          </div>
        </div>
        <div className="relative z-10 space-y-6">
          <h1 className="font-heading text-5xl font-bold leading-tight tracking-tight">
            Ship software the way engineers deserve.
          </h1>
          <p className="text-primary-foreground/80 text-base leading-relaxed max-w-md">
            One place for projects, tasks, bugs, time logs and reports — with role-based access,
            real-time updates, and a workflow that keeps up with your team.
          </p>
          <div className="grid grid-cols-3 gap-4 pt-4 max-w-md">
            {["Projects", "Kanban", "WebSocket"].map((f) => (
              <div key={f} className="border border-primary-foreground/20 rounded-md p-3">
                <div className="text-[10px] uppercase tracking-widest opacity-70">Module</div>
                <div className="font-heading font-semibold mt-1">{f}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-xs opacity-60 font-mono">
          © {new Date().getFullYear()} InfozIT Solutions · Engineering Ops
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md space-y-8">
          <div>
            <h2 className="font-heading text-3xl font-bold tracking-tight">Sign in</h2>
            <p className="text-sm text-muted-foreground mt-2">
              Use your work email to access your InfozIT workspace.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-widest font-semibold">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setErrors((prev) => ({ ...prev, email: "" })); }}
                placeholder="you@company.com"
                data-testid="login-email-input"
                autoComplete="username"
              />
              {errors.email && <span className="text-xs text-red-500 block mt-1">{errors.email}</span>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-widest font-semibold">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setErrors((prev) => ({ ...prev, password: "" })); }}
                  className="pr-10"
                  data-testid="login-password-input"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.password && <span className="text-xs text-red-500 block mt-1">{errors.password}</span>}
            </div>
            <Button type="submit" className="w-full h-11" disabled={loading} data-testid="login-submit-btn">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Sign in
            </Button>
          </form>

          {/* <Card className="p-4 space-y-3">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
              Demo Accounts
            </div>
            <div className="grid grid-cols-2 gap-2">
              {DEMO_ACCOUNTS.map((a) => (
                <button
                  key={a.email}
                  type="button"
                  onClick={() => applyDemo(a)}
                  data-testid={`demo-account-${a.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-left border border-border rounded-md p-2 hover:bg-muted transition-colors"
                >
                  <div className="text-xs font-semibold">{a.label}</div>
                  <div className="text-[10px] text-muted-foreground font-mono truncate">{a.email}</div>
                </button>
              ))}
            </div>
          </Card> */}
        </div>
      </div>
    </div>
  );
}
